import { User } from 'store/auth/models';
import {
  ReceiptEntityStatistics,
  ReceiptStatisticsPayload,
  ReceiptStatisticsTotals,
} from 'store/organisation/models';
import { MappedReturn } from 'store/returns/models/returnData';

const STAT_FIELDS: (keyof ReceiptStatisticsTotals)[] = [
  'uploadedFilesTotal',
  'activeFiles',
  'filesWithErrors',
  'deletedFiles',
  'uploadedManually',
  'uploadedFromGoogleDrive',
  'uploadedFromOneDrive',
  'uploadedFromEmail',
  'uploadedThisMonth',
  'postedFiles',
  'postedThisMonth',
  'filesPendingReview',
  'filesReviewed',
  'transactionsPendingReview',
  'reviewedTransactions',
];

export const emptyReceiptStatisticsTotals = (): ReceiptStatisticsTotals => ({
  uploadedFilesTotal: 0,
  activeFiles: 0,
  filesWithErrors: 0,
  deletedFiles: 0,
  uploadedManually: 0,
  uploadedFromGoogleDrive: 0,
  uploadedFromOneDrive: 0,
  uploadedFromEmail: 0,
  uploadedThisMonth: 0,
  postedFiles: 0,
  postedThisMonth: 0,
  filesPendingReview: 0,
  filesReviewed: 0,
  transactionsPendingReview: 0,
  reviewedTransactions: 0,
});

export const sumEntityStats = (
  entitiesStats: ReceiptEntityStatistics[]
): ReceiptStatisticsTotals => {
  const totals = emptyReceiptStatisticsTotals();
  entitiesStats.forEach((entity) => {
    STAT_FIELDS.forEach((field) => {
      totals[field] += entity[field] ?? 0;
    });
  });
  return totals;
};

export const isUserAssignedToEntity = (
  entityConnectionId: string,
  mappedReturns: MappedReturn[] | undefined,
  currentUser: User | undefined
): boolean => {
  if (!currentUser?.userUuid || !mappedReturns?.length) return false;
  const userId = currentUser.userUuid;
  return mappedReturns.some((mappedReturn) => {
    if (mappedReturn.connectionId !== entityConnectionId) return false;
    const dataLog = mappedReturn.dataLog;
    return (
      dataLog?.preparerUser?.userUuid === userId ||
      dataLog?.reviewerUser?.userUuid === userId ||
      dataLog?.clientUser?.userUuid === userId ||
      mappedReturn.preparer?.userUuid === userId ||
      mappedReturn.reviewer?.userUuid === userId ||
      mappedReturn.client?.userUuid === userId
    );
  });
};

export const filterEntitiesStatsForMyView = (
  entitiesStats: ReceiptEntityStatistics[] | undefined,
  mappedReturns: MappedReturn[] | undefined,
  currentUser: User | undefined
): ReceiptEntityStatistics[] => {
  if (!entitiesStats?.length) return [];
  const filtered = entitiesStats.filter((entity) =>
    isUserAssignedToEntity(
      entity.applicationConnectionId,
      mappedReturns,
      currentUser
    )
  );
  return filtered.length > 0 ? filtered : entitiesStats;
};

export const getDashboardReceiptStatistics = (
  statistics: ReceiptStatisticsPayload | undefined,
  viewType: 'manager' | 'my',
  mappedReturns: MappedReturn[] | undefined,
  currentUser: User | undefined
): ReceiptStatisticsTotals => {
  if (!statistics) return emptyReceiptStatisticsTotals();

  if (viewType === 'manager') {
    return {
      uploadedFilesTotal: statistics.uploadedFilesTotal ?? 0,
      activeFiles: statistics.activeFiles ?? 0,
      filesWithErrors: statistics.filesWithErrors ?? 0,
      deletedFiles: statistics.deletedFiles ?? 0,
      uploadedManually: statistics.uploadedManually ?? 0,
      uploadedFromGoogleDrive: statistics.uploadedFromGoogleDrive ?? 0,
      uploadedFromOneDrive: statistics.uploadedFromOneDrive ?? 0,
      uploadedFromEmail: statistics.uploadedFromEmail ?? 0,
      uploadedThisMonth: statistics.uploadedThisMonth ?? 0,
      postedFiles: statistics.postedFiles ?? 0,
      postedThisMonth: statistics.postedThisMonth ?? 0,
      filesPendingReview: statistics.filesPendingReview ?? 0,
      filesReviewed: statistics.filesReviewed ?? 0,
      transactionsPendingReview: statistics.transactionsPendingReview ?? 0,
      reviewedTransactions: statistics.reviewedTransactions ?? 0,
    };
  }

  const myEntities = filterEntitiesStatsForMyView(
    statistics.entitiesStats,
    mappedReturns,
    currentUser
  );
  return sumEntityStats(myEntities);
};

export const receiptStatisticsDateRangeKey = (
  dateRange?: { from?: string; to?: string }
): string => `${dateRange?.from ?? ''}:${dateRange?.to ?? ''}`;

export const receiptStatisticsEntityCacheKey = (
  connectionId: string,
  dateRange?: { from?: string; to?: string }
): string => `${connectionId}:${receiptStatisticsDateRangeKey(dateRange)}`;

export type ReceiptEntityLookupIds = {
  connectionId?: string;
  organisationId?: string;
};

export const matchesReceiptEntityId = (
  applicationConnectionId: string,
  ids: ReceiptEntityLookupIds
): boolean => {
  const trimmed = applicationConnectionId?.trim();
  if (!trimmed) return false;
  return (
    trimmed === ids.connectionId?.trim() ||
    trimmed === ids.organisationId?.trim()
  );
};

export const findReceiptEntityStats = (
  entitiesStats: ReceiptEntityStatistics[] | undefined,
  ids: ReceiptEntityLookupIds
): ReceiptEntityStatistics | undefined =>
  entitiesStats?.find((entity) =>
    matchesReceiptEntityId(entity.applicationConnectionId, ids)
  );

export const getReceiptEntityStatsFromMap = (
  map: Map<string, ReceiptEntityStatistics>,
  ids: ReceiptEntityLookupIds
): ReceiptEntityStatistics | undefined =>
  (ids.connectionId && map.get(ids.connectionId)) ||
  (ids.organisationId && map.get(ids.organisationId)) ||
  undefined;

/** Map keyed by `applicationConnectionId` (same shape as the Entities list). */
export const buildReceiptStatsByConnectionId = (
  receiptStatistics?: Pick<ReceiptStatisticsPayload, 'entitiesStats'> | null
): Map<string, ReceiptEntityStatistics> => {
  const map = new Map<string, ReceiptEntityStatistics>();
  receiptStatistics?.entitiesStats?.forEach((entityStats) => {
    map.set(entityStats.applicationConnectionId, entityStats);
  });
  return map;
};

export const unwrapReceiptStatisticsPayload = (
  data: Record<string, unknown> | undefined
): ReceiptStatisticsPayload | null => {
  if (!data) return null;
  const nested = data.data as ReceiptStatisticsPayload | undefined;
  const payload = (nested ?? data) as ReceiptStatisticsPayload;
  if (!payload) return null;

  const entitiesStats = Array.isArray(payload.entitiesStats)
    ? payload.entitiesStats
    : [];

  return {
    ...emptyReceiptStatisticsTotals(),
    ...payload,
    entitiesStats,
  };
};

export const entityStatsToTotals = (
  entity: ReceiptEntityStatistics
): ReceiptStatisticsTotals => {
  const { applicationConnectionId: _id, ...totals } = entity;
  return totals;
};
